import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Badge,
  Collapsible,
  ButtonGroup,
} from "@shopify/polaris";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EditIcon,
  DeleteIcon,
} from "@shopify/polaris-icons";
import { AppDispatch, RootState } from "configureStore";
import { deleteOrderReservation } from "../reservations.slice";
import { Reservation } from "services/reservations.service";
import { getReservationStatusBadgeTone } from "./ReservationsSection";
import DataTable from "components/datatable/Datatable";
import useIsPhone from "features/files/use-is-phone";

interface ReservationCardProps {
  reservation: Reservation;
  onEdit: () => void;
  onRefresh: () => void;
}

export default function ReservationCard({
  reservation,
  onEdit,
  onRefresh,
}: ReservationCardProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useParams<{ companyId: string }>();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const isPhone = useIsPhone();

  // Check if user is superadmin (Master)
  const { role } = useSelector((state: RootState) => state.authReducer);
  const isSuperAdmin = role?.permissionsgroups?.includes("Master");

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDelete = async () => {
    if (!companyId) return;

    const confirmed = window.confirm(t("reservations_delete_confirm"));
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteOrderReservation(companyId, reservation.id));
      onRefresh();
    } catch (error) {
      console.error("Failed to delete reservation:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const canEdit = isSuperAdmin || reservation.status === "draft";
  const canDelete = isSuperAdmin || reservation.status === "draft";

  // Prepare items for DataTable
  const itemsCollection = (reservation.items || []).map((item) => {
    const product =
      typeof item.product === "object" && item.product !== null
        ? item.product
        : { id: item.product };
    const fulfilledQty = item.fulfilledQuantity || 0;
    const remainingQty = Math.max(0, (item.quantity || 0) - fulfilledQty);
    return {
      id: item.id,
      productName: (product as any).title || (product as any).id || "-",
      sku: (product as any).sku || "-",
      quantity: item.quantity,
      remaining: remainingQty,
      notes: item.notes || "-",
      phoneNote: item.notes,
    };
  });

  const totals = (reservation.items || []).reduce(
    (acc, item) => {
      const qty = item.quantity || 0;
      const fulfilledQty = item.fulfilledQuantity || 0;
      acc.total += qty;
      acc.fulfilled += fulfilledQty;
      acc.remaining += Math.max(0, qty - fulfilledQty);
      return acc;
    },
    { total: 0, fulfilled: 0, remaining: 0 }
  );

  return (
    <Card>
      <BlockStack gap="300">
        {/* Header */}
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h3" variant="headingSm">
              {t("reservations_number", { number: reservation.number })}
            </Text>
            <Badge tone={getReservationStatusBadgeTone(reservation.status)}>
              {t(`reservations_status_${reservation.status}`)}
            </Badge>
          </InlineStack>
          <InlineStack gap="200">
            <Button
              icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={handleToggleExpand}
              variant="plain"
              accessibilityLabel={isExpanded ? t("collapse") : t("expand")}
            />
          </InlineStack>
        </InlineStack>

        {/* Summary row */}
        <InlineStack gap="400">
          <Text as="span" variant="bodySm" tone="subdued">
            {t("reservations_items_count", { count: reservation.items?.length || 0 })}
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            {t("remaining_to_deliver")}: {Math.round(totals.remaining * 100) / 100}
          </Text>
          {reservation.createdBy && (
            <Text as="span" variant="bodySm" tone="subdued">
              {t("created_by")}: {reservation.createdBy.name}
            </Text>
          )}
        </InlineStack>

        {/* Expanded content */}
        <Collapsible open={isExpanded} id={`reservation-${reservation.id}-content`}>
          <BlockStack gap="300">
            {/* Notes */}
            {reservation.notes && (
              <div>
                <Text as="p" variant="bodySm" fontWeight="semibold">
                  {t("notes")}:
                </Text>
                <Text as="p" variant="bodySm">
                  {reservation.notes}
                </Text>
              </div>
            )}

            {/* Items table */}
            {itemsCollection.length > 0 ? (
              <DataTable
                collection={itemsCollection}
                columns={[
                  { key: "sku", name: t("sku"), mobileView: "bottomLeft" },
                  {
                    key: "productName",
                    name: t("product_name"),
                    mobileView: "topLeft",
                    // The phone card has four slots and no notes column, so the note goes under the name there.
                    type: "custom",
                    render: (row) => (
                      <>
                        {row.productName}
                        {row.phoneNote && (
                          <span className="mt-0.5 block text-xs text-gray-500 md:hidden">{row.phoneNote}</span>
                        )}
                      </>
                    ),
                  },
                  { key: "quantity", name: t("quantity"), type: "number", mobileView: "topRight" },
                  {
                    key: "remaining",
                    name: t("remaining_to_deliver"),
                    type: "number",
                    mobileView: "bottomRight",
                  },
                  { key: "notes", name: t("notes") },
                ]}
                isTotalsDisabled
              />
            ) : (
              <Text as="p" tone="subdued">
                {t("reservations_no_items")}
              </Text>
            )}

            {/* Actions: on a phone full buttons with room between them, so Delete is not hit instead of Edit. */}
            <InlineStack gap="200" align="end">
              <ButtonGroup gap={isPhone ? "loose" : undefined}>
                {canEdit && (
                  <Button
                    icon={<EditIcon />}
                    onClick={onEdit}
                    variant={isPhone ? undefined : "plain"}
                  >
                    {t("edit")}
                  </Button>
                )}
                {canDelete && (
                  <Button
                    icon={<DeleteIcon />}
                    onClick={handleDelete}
                    variant={isPhone ? undefined : "plain"}
                    tone="critical"
                    loading={isDeleting}
                  >
                    {t("delete")}
                  </Button>
                )}
              </ButtonGroup>
            </InlineStack>
          </BlockStack>
        </Collapsible>
      </BlockStack>
    </Card>
  );
}
